import {
  createNormalizedMarkdownCard,
  createNormalizedSectionedHorizontalCard,
} from '@/lib/idea-card-factory';
import type {
  IdeaCardBodySection,
  IdeaCardInputData,
  NormalizedIdeaCardBodySection,
  NormalizedIdeaCardData,
} from '@/lib/idea-card-types';
import {
  cleanIdeaCardTitleText,
  parseSectionedHorizontalBody,
  sanitizeIdeaCardBodyMarkdown,
  serializeIdeaCardBodySections,
} from '@/lib/idea-card-text';
import { toTitleCase } from '@/lib/utils';

const DISPLAY_TITLE_LIMIT = 260;
const URL_TITLE_RE = /^https?:\/\/[^\s]+$/i;

const clampTitle = (value: string): string =>
  value.length > DISPLAY_TITLE_LIMIT ? `${value.slice(0, DISPLAY_TITLE_LIMIT - 3)}...` : value;

const resolveDisplayTitle = (card: IdeaCardInputData): Pick<NormalizedIdeaCardData, 'displayTitle' | 'displayUrl'> => {
  const fallbackSource = card.title || ('body' in card ? card.body : '') || 'Card';
  const cleanedTitle = cleanIdeaCardTitleText(fallbackSource);

  if (!URL_TITLE_RE.test(cleanedTitle)) {
    return { displayTitle: clampTitle(toTitleCase(cleanedTitle)) };
  }

  try {
    const url = new URL(cleanedTitle);
    return {
      displayTitle: clampTitle(url.hostname),
      displayUrl: cleanedTitle,
    };
  } catch {
    return {
      displayTitle: 'Link Details',
      displayUrl: cleanedTitle,
    };
  }
};

const sanitizeSections = (sections: IdeaCardBodySection[]): IdeaCardBodySection[] =>
  sections
    .map((section) => ({
      ...section,
      label: section.label ? cleanIdeaCardTitleText(section.label, 'Section') : undefined,
      content: sanitizeIdeaCardBodyMarkdown(section.content),
    }))
    .filter((section) => section.content);

const toDisplaySections = (sections: IdeaCardBodySection[]): NormalizedIdeaCardBodySection[] =>
  sanitizeSections(sections).map((section) => ({
    ...section,
    displayLabel: section.label ? cleanIdeaCardTitleText(section.label, 'Section') : undefined,
    displayContent: sanitizeIdeaCardBodyMarkdown(section.content),
  }));

export const normalizeIdeaCard = (card: IdeaCardInputData): NormalizedIdeaCardData => {
  const { displayTitle, displayUrl } = resolveDisplayTitle(card);

  if (card.bodyModel === 'sectioned_horizontal') {
    const displayBodySections = toDisplaySections(card.bodySections);

    return createNormalizedSectionedHorizontalCard({
      title: card.title,
      bodySections: displayBodySections.map(({ displayLabel, displayContent, ...section }) => section),
      displayBodySections,
      score: card.score,
      scoreUnit: card.scoreUnit,
      scoreLabel: card.scoreLabel,
      bodyMode: card.bodyMode,
      preview: card.preview,
      groupLabel: card.groupLabel,
      displayTitle,
      displayBody: serializeIdeaCardBodySections(displayBodySections),
      displayUrl,
    });
  }

  const sanitizedBody = card.body ? sanitizeIdeaCardBodyMarkdown(card.body) : undefined;
  const derivedBodySections =
    card.layout === 'horizontal' && sanitizedBody
      ? parseSectionedHorizontalBody(sanitizedBody)
      : undefined;

  if (derivedBodySections && derivedBodySections.length > 0) {
    const displayBodySections = toDisplaySections(derivedBodySections);

    return createNormalizedSectionedHorizontalCard({
      title: card.title,
      bodySections: displayBodySections.map(({ displayLabel, displayContent, ...section }) => section),
      displayBodySections,
      score: card.score,
      scoreUnit: card.scoreUnit,
      scoreLabel: card.scoreLabel,
      bodyMode: card.bodyMode,
      preview: card.preview,
      groupLabel: card.groupLabel,
      displayTitle,
      displayBody: serializeIdeaCardBodySections(displayBodySections),
      displayUrl,
    });
  }

  return createNormalizedMarkdownCard({
    title: card.title,
    body: sanitizedBody,
    score: card.score,
    scoreUnit: card.scoreUnit,
    scoreLabel: card.scoreLabel,
    layout: card.layout,
    bodyMode: card.bodyMode,
    preview: card.preview,
    groupLabel: card.groupLabel,
    displayTitle,
    displayBody: sanitizedBody,
    displayUrl,
  });
};

export const normalizeIdeaCards = (cards: IdeaCardInputData[]): NormalizedIdeaCardData[] =>
  cards.map(normalizeIdeaCard);
